import { useQueryClient } from '@tanstack/react-query';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';

import { OpsRotaApiError } from '@/services/opsRota/client';
import { opsRotaQueryKeys } from '@/services/opsRota/queryKeys';
import type { OpsSchedule } from '@/services/opsRota/types';
import styled from '@/styles';
import { trackOpsScheduleChanged, trackOpsScheduleFailed } from '@/util/vexo';

import { failReason, isOvernight } from '../analytics';
import { useOpsManagers, useScheduleMutations } from '../hooks/useOpsRota';
import {
  SAVE_FAILED,
  savedToast,
  STALE_MESSAGE,
  updatedToast,
} from '../messages';
import {
  LinkButton,
  PrimaryButton,
  SecondaryButton,
  TextButton,
} from '../Operations.styles';
import {
  canSave,
  EMPTY_FORM,
  fromSchedule,
  isDirty,
  isInactiveManager,
  toTimes,
} from '../scheduleForm';

import ErrorList from './ErrorList';
import ScheduleFields from './ScheduleFields';

export type ScheduleFormProps = {
  /** First (or only) date, "YYYY-MM-DD", ops zone. */
  date: string;
  /** Last date of a range, or null for a single date. */
  endDate: string | null;
  /** Set in edit mode: the block being edited (prefill and lock_version). */
  schedule?: OpsSchedule;
  /**
   * After a successful save. The page shows the toast and leaves the form
   * (`addAnother` false) or opens a blank one (`addAnother` true).
   */
  onSaved: (
    title: string,
    body: string | undefined,
    addAnother: boolean,
  ) => void;
  /** Cancel: back to the day's block list. */
  onCancel: () => void;
  /** Report unsaved changes; drives the discard guard. */
  onDirtyChange: (dirty: boolean) => void;
  /** Show Cancel (UI spec §8.1 `showCancel`). */
  canCancel: boolean;
  /**
   * Opens the roster modal from an empty roster's dropdown. `trigger`
   * is the manager field, where focus returns when the roster closes.
   */
  onManageManagers?: (trigger: HTMLElement | null) => void;
  /** Report a save in flight; the page locks itself meanwhile. */
  onBusyChange?: (busy: boolean) => void;
};

// UI spec §9: fields on top (flex 1), the action row pinned to the bottom of
// the panel.
const Form = styled('form', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  minWidth: 0,
});

const Top = styled('div', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

// Right-aligned, gap 16 (§8.1); wraps on a narrow panel instead of
// overflowing (§5 flags the prototype's overflow at 1024).
const Actions = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: '12px 16px',
});

type FormError = { messages: string[]; stale: boolean };

const toFormError = (err: unknown, editing: boolean): FormError => {
  if (!(err instanceof OpsRotaApiError)) {
    return { messages: [SAVE_FAILED], stale: false };
  }
  // 409: changed meanwhile. 404 on edit: deleted meanwhile. Same advice.
  if (err.isConflict || (editing && err.status === 404)) {
    return { messages: [STALE_MESSAGE], stale: true };
  }
  return { messages: err.messages, stale: false };
};

/**
 * The Operations panel's add / edit form (GRAVY-10878 F4): Start and End
 * (quarter hours), the managers, then Save / Add another / Cancel. Server
 * errors show inline and the form keeps its values.
 */
const ScheduleForm = ({
  date,
  endDate,
  schedule,
  onSaved,
  onCancel,
  onDirtyChange,
  canCancel,
  onManageManagers,
  onBusyChange,
}: ScheduleFormProps) => {
  const initial = useMemo(
    () => (schedule ? fromSchedule(schedule) : EMPTY_FORM),
    [schedule],
  );
  const [values, setValues] = useState(initial);
  const [error, setError] = useState<FormError | null>(null);
  // Which button sent the pending save (its label reads "Saving…"); the
  // ref stops a second submit before the re-render disables the buttons.
  const [savingWith, setSavingWith] = useState<'save' | 'another' | null>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const managersQuery = useOpsManagers();
  const { create, update } = useScheduleMutations();
  const queryClient = useQueryClient();

  const roster = managersQuery.data;
  // BR-06: a deactivated manager blocks the save until its chip is removed.
  const hasInactive = values.managerIds.some((id) =>
    isInactiveManager(id, roster, schedule?.managers),
  );
  const busy = create.isPending || update.isPending;
  const dirty = isDirty(values, initial);
  // An edit that changes nothing still moves graphy's
  // lock_version, so another admin editing the block would get a false 409.
  const valid = canSave(values) && !hasInactive && (!schedule || dirty);

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  // Gone means nothing is unsaved (a save, a cancel, or the day changing).
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  useEffect(() => onBusyChange?.(busy), [busy, onBusyChange]);
  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const submit = async (addAnother: boolean) => {
    if (!valid || busy || inFlight.current) return;
    inFlight.current = true;
    setSavingWith(addAnother ? 'another' : 'save');
    setError(null);
    try {
      // Count the managers on the block graphy saved. Identical times
      // merge into an existing block (BR-03), which then holds more people
      // than this form sent.
      if (schedule) {
        const saved = await update.mutateAsync({
          id: schedule.id,
          input: { ...toTimes(values), lock_version: schedule.lock_version },
        });
        trackOpsScheduleChanged({
          action: 'update',
          days_count: 1,
          managers_count: saved.schedule.managers.length,
          merged: saved.merged,
          overnight: isOvernight(saved.schedule),
        });
        const t = updatedToast(saved.schedule.managers.length, schedule.date);
        if (mounted.current) onSaved(t.title, t.body, false);
      } else {
        const saved = await create.mutateAsync({
          date,
          ...(endDate && endDate !== date ? { end_date: endDate } : {}),
          ...toTimes(values),
        });
        trackOpsScheduleChanged({
          action: 'create',
          days_count: saved.dates.length,
          managers_count: saved.schedule.managers.length,
          merged: saved.merged,
          overnight: isOvernight(saved.schedule),
          add_another: addAnother,
        });
        // A range answers with its first date's block only, which may also
        // hold people merged in on that one date. Every date got exactly the
        // managers sent (graphy refuses the whole range on any clash), so
        // that is the count for a range; one date keeps the block's.
        const isRange = saved.dates.length > 1;
        const t = savedToast(
          isRange ? values.managerIds.length : saved.schedule.managers.length,
          date,
          endDate,
        );
        if (mounted.current) onSaved(t.title, t.body, addAnother);
      }
    } catch (err) {
      trackOpsScheduleFailed({
        action: schedule ? 'update' : 'create',
        ...failReason(err, Boolean(schedule)),
      });
      if (mounted.current) setError(toFormError(err, Boolean(schedule)));
    } finally {
      inFlight.current = false;
      setSavingWith(null);
    }
  };

  // The mutation already invalidated on settle; this makes sure the page
  // refetches, then leaves the form for the fresh list.
  const reload = () => {
    queryClient.invalidateQueries({ queryKey: opsRotaQueryKeys.all });
    onCancel();
  };

  const onSubmit = (ev: FormEvent) => {
    ev.preventDefault();
    submit(false);
  };

  return (
    // Kept out of HubSpot's collected-forms capture, like the roster form.
    <Form
      data-hs-do-not-collect="true"
      data-testid={schedule ? 'opsEditForm' : 'opsAddForm'}
      data-mode={schedule ? 'edit' : 'add'}
      aria-label={schedule ? 'Edit schedule' : 'Add schedule'}
      aria-busy={busy}
      noValidate
      onSubmit={onSubmit}
    >
      <Top>
        <ScheduleFields
          idPrefix="ops"
          values={values}
          onChange={setValues}
          roster={roster}
          rosterError={managersQuery.isError}
          onRetryRoster={() => managersQuery.refetch()}
          assigned={schedule?.managers}
          disabled={busy}
          onManageManagers={onManageManagers}
        />
        {error && (
          <ErrorList
            messages={error.messages}
            testId="opsFormError"
            focusOnShow
          >
            {error.stale && (
              <LinkButton
                type="button"
                data-testid="opsReload"
                onClick={reload}
              >
                Reload
              </LinkButton>
            )}
          </ErrorList>
        )}
      </Top>
      <Actions>
        {(canCancel || schedule) && (
          <TextButton
            type="button"
            disabled={busy}
            data-testid="opsCancelButton"
            onClick={onCancel}
          >
            Cancel
          </TextButton>
        )}
        {!schedule && (
          <SecondaryButton
            type="button"
            disabled={!valid || busy}
            data-testid="opsAddAnotherButton"
            onClick={() => submit(true)}
          >
            {savingWith === 'another' ? 'Saving…' : 'Add another schedule'}
          </SecondaryButton>
        )}
        <PrimaryButton
          type="submit"
          disabled={!valid || busy}
          data-testid="opsSaveButton"
        >
          {savingWith === 'save'
            ? 'Saving…'
            : schedule
              ? 'Save changes'
              : 'Save schedule'}
        </PrimaryButton>
      </Actions>
    </Form>
  );
};

export default ScheduleForm;
